import React, { useState, useRef, forwardRef } from 'react';
import dayjs from 'dayjs';
import useControllableState from '../hooks/useControllableState';
import useMergedRef from '../hooks/useMergeRef';
import RangeCalendar from './RangeCalendar';
import BasePicker from './BasePicker';
import { useConfig } from '../ConfigProvider';

/* =====================================================
   Helpers
===================================================== */

const toDate = (val) => {
  if (!val) return null;
  if (val instanceof Date) return val;
  return dayjs(val).toDate();
};

const formatDate = (date, locale, format) =>
  date ? dayjs(date).locale(locale).format(format) : '';

/* =====================================================
   Component
===================================================== */

const DatePickerRange = forwardRef((props, ref) => {
  const {
    className,
    clearable,
    clearButton,
    dateViewCount,
    dayClassName,
    dayStyle,
    defaultMonth,
    defaultOpen,
    defaultValue,
    defaultView,
    disabled,
    disableDate,
    enableHeaderLabel,
    disableOutOfMonth,
    firstDayOfWeek,
    hideOutOfMonthDates,
    hideWeekdays,
    inputFormat = 'YYYY-MM-DD',
    inputPrefix,
    inputSuffix,
    labelFormat,
    seperator = '→',
    locale,
    maxDate,
    minDate,
    onChange,
    renderDay,
    singleDate,
    size,
    style,
    value,
    weekendDays,
    yearLabelFormat,
    ...rest
  } = props;

  const { locale: themeLocale } = useConfig();
  const finalLocale = locale || themeLocale;

  const inputRef = useRef();
  const [dropdownOpened, setDropdownOpened] = useState(defaultOpen);

  /* =====================================================
     State
  ===================================================== */

  const [_valueRaw, setValueRaw] = useControllableState({
    prop: value,
    defaultProp: defaultValue ?? [null, null],
  });

  const _value = [
    toDate(_valueRaw?.[0]),
    toDate(_valueRaw?.[1]),
  ];

  const first = _value[0];
  const second = _value[1];

  const hasValue = !!(first || second);

  /* =====================================================
     Handlers
  ===================================================== */

  const handleValueChange = (range) => {
    let [start, end] = range || [];

    // first click → set same day
    if (start && !end) {
      end = start;
    }

    const finalRange = [start, end];

    setValueRaw(finalRange);

    onChange?.([
      formatDate(start, finalLocale, inputFormat),
      formatDate(end, finalLocale, inputFormat),
    ]);

    // ❌ NO AUTO CLOSE ANYMORE
  };

  const handleClear = () => {
    setValueRaw([null, null]);
    onChange?.(['', '']);
    inputRef.current?.focus();
  };

  /* =====================================================
     Label Logic
  ===================================================== */

  const firstLabel = formatDate(first, finalLocale, inputFormat);
  const secondLabel = formatDate(second, finalLocale, inputFormat);

  let inputLabel = '';

  if (first && second && first.getTime() !== second.getTime()) {
    inputLabel = `${firstLabel} ${seperator} ${secondLabel}`;
  } else if (first) {
    inputLabel = firstLabel;
  }

  /* =====================================================
     Render
  ===================================================== */

  return (
    <BasePicker
      dropdownOpened={dropdownOpened}
      setDropdownOpened={setDropdownOpened}
      ref={useMergedRef(ref, inputRef)}
      size={size}
      style={style}
      className={className}
      inputLabel={inputLabel}
      placeholder="Date Range"
      clearable={clearable && hasValue}
      clearButton={clearButton}
      onClear={handleClear}
      disabled={disabled}
      inputPrefix={inputPrefix}
      inputSuffix={inputSuffix}
      {...rest}
    >
      <div style={{ position: 'relative' }}>
        {hasValue && (
          <button
            type="button"
            onClick={handleClear}
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 10,
              width: 26,
              height: 26,
              borderRadius: 999,
              border: 'none',
              cursor: 'pointer',
              background: '#f3f4f6',
              fontSize: 16,
              fontWeight: 600,
              lineHeight: '26px',
            }}
          >
            ✕
          </button>
        )}

        <RangeCalendar
          locale={finalLocale}
          defaultMonth={first || new Date()}
          value={[first, second]}
          onChange={handleValueChange}
          labelFormat={labelFormat}
          dayClassName={dayClassName}
          dayStyle={dayStyle}
          disableOutOfMonth={disableOutOfMonth}
          minDate={minDate}
          maxDate={maxDate}
          disableDate={disableDate}
          firstDayOfWeek={firstDayOfWeek}
          enableHeaderLabel={enableHeaderLabel}
          singleDate={singleDate}
          dateViewCount={dateViewCount}
          defaultView={defaultView}
          hideOutOfMonthDates={hideOutOfMonthDates}
          hideWeekdays={hideWeekdays}
          renderDay={renderDay}
          weekendDays={weekendDays}
          yearLabelFormat={yearLabelFormat}
        />
      </div>
    </BasePicker>
  );
});

DatePickerRange.defaultProps = {
  labelFormat: { month: 'MMM', year: 'YYYY' },
  defaultOpen: false,
  seperator: '→',
  clearable: true,
  firstDayOfWeek: 'monday',
  singleDate: false,
  dateViewCount: 1,
};

export default DatePickerRange;
